import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setSelectedBillId } from "../../store";
import {
  useEnabledItems,
  useMenuAddons,
  useMenuModifiers,
  useLunchCatalogRefresh,
  useLunchMenuCheck,
  useMenu,
} from "../../hooks/useMenu";
import { useBills, useCreateBill } from "../../hooks/useBills";
import { getApiErrorMessage } from "../../utils/translations";
import {
  useConfiguration,
  useUpdateUserConfiguration,
} from "../../hooks/useConfiguration";
import { useQueryClient } from "@tanstack/react-query";
import { useOrderingMessage } from "./components/useOrderingMessage";
import OrderingTopBar from "./components/OrderingTopBar";
import { OrderingShell } from "./components/OrderingShell";
import { useIsEdgeLocal } from "../../hooks/useEdgeMode";
import { useFirstClickFullscreen } from "../../components/profileActions";
import {
  OrderingSkeleton,
  TableRailSkeleton,
} from "./components/OrderingSkeleton";
import { CategoryChips } from "./components/menu/CategoryChips";
import { StationChips } from "./components/menu/StationChips";
import type { PreviewFilter } from "./components/menu/PreviewFilterChips";
import type { CatalogView } from "../../api/configuration";
import { ItemCatalog } from "./components/menu/ItemCatalog";
import { OrderFooter } from "./components/menu/OrderFooter";
import { ItemDetailModal } from "./components/menu/ItemDetailModal";
import { EditModifiersModal } from "./components/menu/EditModifiersModal";
import { ModifyItemModal } from "./components/menu/ModifyItemModal";
import { useOrderLineEdit } from "./useOrderLineEdit";
import { DiscardOrderModal } from "./components/menu/DiscardOrderModal";
import { TableList } from "./components/tables/TableList";
import { useTableListBills } from "./components/tables/useTableListBills";
import { OrderDetails } from "./components/tables/OrderDetails";
import { sortBillElementsForDisplay } from "./components/tables/billSummaryUtils";
import {
  useOrderItemsKeyboard,
  type KeyboardOrderItem,
} from "./components/tables/useOrderItemsKeyboard";
import { TableFooter } from "./components/tables/TableFooter";
import { TableRail } from "./components/tables/TableRail";
import { NewTableModal } from "./components/tables/manage/NewTableModal";
import { useNewTableGuests } from "./components/tables/guests/useNewTableGuests";
import { ClientsModal } from "./components/tables/clients/ClientsModal";
import { VoiceOrderModal } from "./components/tables/voice/VoiceOrderModal";
import { useVoiceSeat } from "./components/tables/voice/useVoiceSeat";
import { useReservationConfirm } from "./components/tables/manage/useReservationConfirm";
import { useOpenNextTable } from "./components/tables/manage/useOpenNextTable";
import { OrderPanel } from "./components/menu/OrderPanel";
import { MobileOrderBar } from "./components/menu/MobileOrderBar";
import { OrderSheet } from "./components/menu/OrderSheet";
import type { CreateBillIn, NewBillDiscountIn } from "../../api/bills";
import { isCloudUnreachableError } from "../../api/client";
import { isLunchItemOnSale, lunchItemId, sourceItemIdOf } from "../../lib/lunchCatalog";
import { getEndedLunchMenuIds } from "../../api/lunchMenus";
import { useEndedLunchCartItems } from "./lunch/useEndedLunchCartItems";
import { useCloseEndedLunchModals } from "./lunch/useCloseEndedLunchModals";
import { useLunchLimits } from "./lunch/useLunchLimits";
import type { MenuResponse } from "../../api/menu";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../hooks/useViewport";

type ViewMode = "tables" | "products";

interface OrderItem {
  itemId: number;
  quantity: number;
  modifiers: number[];
  addons: Record<number, Record<number, number>>; // addonId -> { itemId -> количество }
  comment: string;
}

// Колко старо меню още е „свежо" при отваряне на масите / клик между масите
// (бързи последователни кликове не пращат по заявка всеки)
const LUNCH_CHECK_MAX_AGE_MS = 3000;

const Ordering = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  // Първи клик в модула → цял екран (без админския акаунт)
  useFirstClickFullscreen();
  const { isLoading: menuLoading } = useMenu();
  useLunchCatalogRefresh();
  const checkLunchMenus = useLunchMenuCheck();
  const allItems = useEnabledItems();
  const allAddons = useMenuAddons();
  const allModifiers = useMenuModifiers();
  const message = useOrderingMessage();
  // Бързото отваряне (следващ номер) пита „масата има резервация — продължаваш ли?"
  const { confirmNotice, offerFreeTable, reservationConfirmModal } = useReservationConfirm();

  const [viewMode, setViewMode] = useState<ViewMode>("tables");
  const {
    data: bills = [],
    isLoading: billsLoading,
    isFetching: billsFetching,
  } = useBills();
  const selectedBillId = useAppSelector((state) => state.bills.selectedBillId);
  // Масите на списъка: закритите без бон по настройката, а тези, чието
  // закриване още чака принтера — винаги и на мястото си (useTableListBills).
  const { tableBills, closingBillIds, showClosedTables } = useTableListBills({
    bills,
    isTablesView: viewMode === "tables",
    billsFetching,
    selectedBillId,
  });
  const selectedBill = tableBills.find((b) => b.id === selectedBillId) ?? null;
  const isSelectedBillClosing =
    selectedBillId != null && closingBillIds.has(selectedBillId);
  const createBillMutation = useCreateBill();
  const [newTableModalOpen, setNewTableModalOpen] = useState(false);
  // „Брой гости" преди „Нова маса" (Конфигурация → Маркиране)
  const {
    newTableGuests,
    askBeforeNewTable,
    guestCountModal,
    guestCountModalOpen,
  } = useNewTableGuests();
  // Гласова поръчка — микрофонът във футъра на масите
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  // „Клиенти" от футъра на продуктовия изглед — същият модал като на екрана
  // с масите; при съществуваща сметка прилага веднага, при нова маса изборът
  // изчаква в pendingDiscount и пътува с CreateBillIn при „Поръчай"
  const [clientsModalOpen, setClientsModalOpen] = useState(false);
  const [pendingDiscount, setPendingDiscount] =
    useState<NewBillDiscountIn | null>(null);
  const [renameTableModalOpen, setRenameTableModalOpen] = useState(false);
  const [footerModalOpen, setFooterModalOpen] = useState(false);
  const [newTableName, setNewTableName] = useState<string | null>(null);
  const [selectedOrderItemId, setSelectedOrderItemId] = useState<number | null>(
    null,
  );
  // „Обобщено" в детайлите на масата — вдигнато тук, защото изборът на ред
  // (selectedOrderItemId) също е тук: обобщеният ред слива няколко записа →
  // изборът става двусмислен и се чисти при включване.
  const [billSummarized, setBillSummarized] = useState(false);
  const toggleBillSummarized = () => {
    if (!billSummarized) setSelectedOrderItemId(null);
    setBillSummarized(!billSummarized);
  };
  const [orderComment, setOrderComment] = useState("");
  const [showCommentInput, setShowCommentInput] = useState(false);

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null,
  );
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modifyModalOpen, setModifyModalOpen] = useState(false);
  // Режим на ModifyItemModal при отваряне с двоен клик от историята на маса —
  // потвърждението поръчва ДИРЕКТНО към сметката, без да минава през кошницата.
  const [reorderMode, setReorderMode] = useState(false);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  // Обедното меню още активно ли е — при отваряне на масите, клик между масите
  // и „Добави"/нова маса (преминаване към продуктите — винаги свежо): друг
  // терминал може да го е приключил/изтрил, а часът може да е минал
  useEffect(() => {
    void checkLunchMenus(viewMode === "products" ? 0 : LUNCH_CHECK_MAX_AGE_MS);
  }, [viewMode, selectedBillId, checkLunchMenus]);
  useEndedLunchCartItems({
    allItems,
    orderItems,
    setOrderItems,
    selectedCategoryId,
    setSelectedCategoryId,
    message,
  });
  // Сигнал за ItemCatalog да зачисти търсачката 1 сек след като ModifyItemModal е бил затворен с "Добави".
  const [modifyAddSignal, setModifyAddSignal] = useState(0);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const closeItemModals = useCallback(() => {
    setModalOpen(false);
    setModifyModalOpen(false);
    setReorderMode(false);
  }, []);
  const lunchLimits = useLunchLimits({ allItems, orderItems, message });
  // Ред в редакция през молива (прегледа или каталога) — EditModifiersModal
  const orderLineEdit = useOrderLineEdit({
    orderItems,
    setOrderItems,
    allowedQuantity: lunchLimits.allowedQuantity,
  });
  const editModifiersTarget = orderLineEdit.target;
  const editModifiersOpen = editModifiersTarget !== null;
  useCloseEndedLunchModals({
    allItems,
    selectedItemId,
    itemModalOpen: modalOpen || modifyModalOpen,
    closeItemModals,
    editItemId: editModifiersTarget?.itemId ?? null,
    closeEditModal: orderLineEdit.close,
    message,
  });
  // Мобилен bottom sheet „Преглед на поръчката" (отваря се от MobileOrderBar)
  const [sheetOpen, setSheetOpen] = useState(false);
  // Скрита релса с маси на тесен екран — двоен клик на маса я скрива,
  // хамбургерът в детайла я връща (демо UX; чисто UI състояние).
  const [railHidden, setRailHidden] = useState(false);
  const { isDesktop, isNarrow } = useViewport();
  // Локален режим (docs/edge.md): гласовите поръчки са извън обхвата на
  // касата — микрофонът остава, но disabled с подсказка. „Клиенти" (и
  // отстъпката при нова маса) касата сервира.
  const edgeLocal = useIsEdgeLocal();
  // Ref към актуалния submit handler — клавиатурният ефект го вика при Enter,
  // без да влачи всичките му зависимости (orderComment, кошницата и т.н.) в deps.
  const submitOrderRef = useRef<() => void>(() => {});
  // Ref към актуалния handleBack — за Esc ефекта под десктоп прага (виж долу).
  const handleBackRef = useRef<() => void>(() => {});
  // Ref към актуалния handleOrderRowReorder — „+" на избран ред го вика от
  // клавиатурния hook, който седи преди ранния return (menuLoading).
  const orderRowReorderRef = useRef<
    (productId: number, lunchMenuId?: number | null) => void
  >(() => {});

  // ID-тата на продуктите, за които инкрементът отваря ModifyItemModal
  // (задължителни/препоръчителни добавки ИЛИ задължителен модификатор).
  const itemsRequiringAddons = useMemo(() => {
    const set = new Set<number>();
    const requiredModifierIds = new Set(
      allModifiers.filter((m) => m.isRequired).map((m) => m.id),
    );
    for (const item of allItems) {
      const has =
        item.addonIds.some((addonId) => {
          const addon = allAddons.find((a) => a.id === addonId);
          return String(addon?.type) === "2" || String(addon?.type) === "3";
        }) || item.modifierIds.some((id) => requiredModifierIds.has(id));
      if (has) set.add(item.id);
    }
    return set;
  }, [allItems, allAddons, allModifiers]);

  // Всички настройки идват с /user/info (auth.user.configuration).
  const { configuration } = useConfiguration();
  // Изглед на каталога — ЛИЧНА настройка на потребителя (org базата, следва
  // го на всяко устройство). Бутонът Плочки/Таблица във футъра (F7) я записва
  // директно (оптимистично, без localStorage) — при следващо влизане е същата.
  const { mutate: updateUserConfiguration } = useUpdateUserConfiguration();
  const catalogView = configuration.catalogView;
  const handleCatalogViewChange = (view: CatalogView) => {
    if (view !== catalogView) updateUserConfiguration({ catalogView: view });
  };
  // Кое стои над каталога (групи/станции) — другото отива над прегледа
  // (настройка на ресторанта, Конфигурация → Маркиране)
  const catalogFilter = configuration.catalogFilter;
  // Филтър по станция — от реда над каталога (при "stations") или от реда
  // над прегледа (при "groups"); и в двата случая филтрира КАТАЛОГА
  const [selectedStationId, setSelectedStationId] = useState<number | null>(
    null,
  );
  // Редът над прегледа показва другото (групи при "stations" и обратно) и е
  // същият филтър на каталога (групите там са дърво като над каталога)
  const previewFilterKind: "station" | "group" =
    catalogFilter === "stations" ? "group" : "station";
  const previewFilter: PreviewFilter = useMemo(() => {
    if (previewFilterKind === "station") {
      return selectedStationId === null
        ? null
        : { kind: "station", id: selectedStationId };
    }
    return selectedCategoryId === null
      ? null
      : { kind: "group", id: selectedCategoryId };
  }, [previewFilterKind, selectedStationId, selectedCategoryId]);
  const handlePreviewFilterChange = (value: PreviewFilter) => {
    if (previewFilterKind === "station") {
      setSelectedStationId(value?.id ?? null);
    } else {
      setSelectedCategoryId(value?.id ?? null);
    }
  };
  // Екранна клавиатура за търсачката (F8 / бутонът във футъра), само десктоп;
  // при излизане от продуктовия изглед се скрива
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  useEffect(() => {
    if (viewMode !== "products") setKeyboardOpen(false);
  }, [viewMode]);
  // Бързо закриване (Конфигурация → Маркиране): „Нова маса" взима автоматично
  // най-малкия свободен номер (без модал), а след „Поръчай" се отваря „Закрий".
  const quickCloseEnabled = configuration.orderingQuickClose;
  // Номерация на масите (Конфигурация → Маркиране). „Обща номерация" ВКЛЮЧЕНА
  // (по подразбиране) = номерата са общи за ресторанта. Изключена = лична
  // номерация: всеки сервитьор си има своя и чужда маса „1" не брои за заета
  // (BE проверява по същия начин).
  const personalNumberingEnabled = !configuration.orderingSharedNumbering;
  const currentUserId = useAppSelector((state) => state.auth.userId);
  // Бързото отваряне: следващият свободен номер без модал за име (пита при
  // резервация). `handleNewTableSubmit` е по-долу — затова през затваряне.
  const openNextTable = useOpenNextTable({
    bills,
    personalNumberingEnabled,
    currentUserId,
    message,
    confirmNotice,
    offerFreeTable,
    onOpen: (tableName) => handleNewTableSubmit(tableName),
  });
  // Цялата гласова функционалност е зад правото open_bill_voice: без него
  // микрофонът не се рендира и модалът няма как да се отвори
  // (BE-то допълнително пази endpoint-а със същото право → 403).
  const permissions = useAppSelector((state) => state.auth.permissions);
  const canVoiceOrder = permissions.some(
    (permission) => permission.name === "open_bill_voice",
  );
  // „Настани" (от „Опции" и от гласа): сметката вече е в списъка (модалът
  // изчаква презареждането) — избираме я и влизаме направо в менюто, както „Добави".
  const handleReservationSeated = (billId: number) => {
    dispatch(setSelectedBillId(billId));
    setSelectedItemId(null);
    setViewMode("products");
  };
  // Гласовото „настани резервацията на …" (voice/useVoiceSeat.tsx).
  const voiceSeat = useVoiceSeat({ onSeated: handleReservationSeated });
  // „Клиенти" в продуктовия изглед — същото право като бутона на масите
  const canSelectCustomer = permissions.some(
    (permission) => permission.name === "select_customer",
  );
  // Активен клиентски процент по сметката (знаков: >0 отстъпка, <0 надбавка) —
  // каталогът показва коригирани цени, панелът — блока с клиента и основата
  const billDiscount =
    selectedBill?.discount_applied
      ? {
          percent: selectedBill.discount_percent ?? 0,
          clientName: selectedBill.discount_applied?.client_name ?? null,
        }
      : pendingDiscount
        ? // Нова маса: чакащият клиент се показва като приложен още преди
          // „Поръчай" (каталогът/панелът с коригирани цени)
          {
            percent: pendingDiscount.percent,
            clientName: pendingDiscount.clientName,
          }
        : null;
  // Сметка, за която да се отвори модалът „Закрий" веднага след поръчката
  // (bills списъкът първо трябва да се refetch-не — TableFooter изчаква).
  const [pendingCloseBillId, setPendingCloseBillId] = useState<number | null>(
    null,
  );
  // Refetch bills when switching to tables view
  useEffect(() => {
    if (viewMode === "tables") {
      queryClient.invalidateQueries({ queryKey: ["bills"] });
    }
  }, [viewMode, queryClient]);

  // F7 (Плочки/Таблица) и F8 (екранна клавиатура) — само в продуктовия
  // изглед на десктоп; не зависят от избран продукт
  useEffect(() => {
    if (viewMode !== "products" || !isDesktop) return;
    const handleViewKeys = (event: KeyboardEvent) => {
      if (event.key === "F7") {
        event.preventDefault();
        updateUserConfiguration({
          catalogView: catalogView === "grid" ? "list" : "grid",
        });
      } else if (event.key === "F8") {
        event.preventDefault();
        setKeyboardOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", handleViewKeys);
    return () => window.removeEventListener("keydown", handleViewKeys);
  }, [viewMode, isDesktop, catalogView, updateUserConfiguration]);

  // Keyboard shortcuts for Info and Modify
  useEffect(() => {
    if (
      viewMode !== "products" ||
      !selectedItemId ||
      modalOpen ||
      modifyModalOpen ||
      discardModalOpen ||
      sheetOpen ||
      clientsModalOpen ||
      editModifiersOpen
    ) {
      return;
    }

    const handleKeyPress = (event: KeyboardEvent) => {
      // Ignore if input/textarea is focused — освен полето за търсене на
      // каталога (настройката orderingSearchField): от него Enter подава
      // поръчката и F9 отваря „Промени", както от списъка.
      if (
        event.target instanceof HTMLElement &&
        (event.target.tagName === "INPUT" ||
          event.target.tagName === "TEXTAREA") &&
        event.target.dataset.catalogSearch === undefined
      ) {
        return;
      }

      const key = event.key.toLowerCase();

      if (key === "enter") {
        event.preventDefault();
        // Enter подава поръчката директно (summary екранът отпадна —
        // панелът „Преглед на поръчката" е винаги видим).
        submitOrderRef.current();
      }

      if (event.key === "F9") {
        event.preventDefault();
        setModifyModalOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [
    viewMode,
    selectedItemId,
    modalOpen,
    modifyModalOpen,
    discardModalOpen,
    sheetOpen,
    clientsModalOpen,
    editModifiersOpen,
    orderItems,
    message,
    t,
  ]);

  // Esc в продуктовия изглед ПОД десктоп прага: клавиатурната машина на
  // ItemCatalog (която на десктоп хваща Esc → назад към масите) там я няма, а
  // глобалната Esc-навигация на общия терминал (SharedAccountGate) би
  // прескочила направо към „Модули"/ПИН. Затова тук връщаме стъпката
  // „продукти → маси" (с discard guard-а на handleBack) и на таблет/телефон
  // с хардуерна клавиатура. Esc никога не прескача нива.
  useEffect(() => {
    if (isDesktop || viewMode !== "products") return;
    if (
      modalOpen ||
      modifyModalOpen ||
      discardModalOpen ||
      sheetOpen ||
      clientsModalOpen ||
      editModifiersOpen
    )
      return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (
        e.target instanceof HTMLElement &&
        (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
      )
        return;
      e.preventDefault();
      handleBackRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    isDesktop,
    viewMode,
    modalOpen,
    modifyModalOpen,
    discardModalOpen,
    sheetOpen,
    clientsModalOpen,
    editModifiersOpen,
  ]);

  // Модал/popover над екрана с масите — стрелките (маси и редове) мълчат.
  const tablesModalOpen =
    modalOpen ||
    modifyModalOpen ||
    newTableModalOpen ||
    guestCountModalOpen ||
    renameTableModalOpen ||
    footerModalOpen ||
    voiceModalOpen ||
    voiceSeat.seatModalOpen;

  // Редовете на избраната маса за стрелките ↑ ↓ — в реда на показване в
  // OrderDetails; в „Обобщено" редовете не са избираеми → празен списък.
  const keyboardOrderItems = useMemo<KeyboardOrderItem[]>(
    () =>
      !selectedBill || billSummarized
        ? []
        : sortBillElementsForDisplay(selectedBill.elements).flatMap(
            (element) =>
              element.items.map((item) => ({
                id: item.id,
                productId: item.item_id,
                lunchMenuId: item.lunch_menu_id,
              })),
          ),
    [selectedBill, billSummarized],
  );
  const orderItemsKeyboardActive = keyboardOrderItems.some(
    (item) => item.id === selectedOrderItemId,
  );
  const handleOrderItemsReorderKey = useCallback(
    (productId: number, lunchMenuId?: number | null) =>
      orderRowReorderRef.current(productId, lunchMenuId),
    [],
  );
  useOrderItemsKeyboard({
    enabled: isDesktop && viewMode === "tables" && !tablesModalOpen,
    items: keyboardOrderItems,
    selectedItemId: selectedOrderItemId,
    onSelect: setSelectedOrderItemId,
    onReorder: handleOrderItemsReorderKey,
  });
  // → от десния край на масите: фокусът минава на първия ред на сметката
  const handleArrowRightEdge = useCallback(() => {
    if (keyboardOrderItems.length === 0) return false;
    setSelectedOrderItemId(keyboardOrderItems[0].id);
    return true;
  }, [keyboardOrderItems]);

  if (menuLoading) {
    // Истинският топ бар се показва веднага (не зависи от менюто) —
    // скелетонът покрива само съдържанието под него.
    return (
      <OrderingShell>
        <OrderingTopBar />
        <OrderingSkeleton label={t("ordering.loadingMenu")} />
      </OrderingShell>
    );
  }

  const selectedItem = allItems.find((item) => item.id === selectedItemId);

  // Етикет на масата за реда с категориите на продуктовия изглед (телефон)
  const tableLabel = newTableName ?? selectedBill?.name ?? "";

  const performBack = () => {
    setViewMode("tables");
    setSelectedCategoryId(null);
    setSelectedItemId(null);
    setOrderItems([]);
    setNewTableName(null);
    setPendingDiscount(null);
    setOrderComment("");
    setShowCommentInput(false);
    setSheetOpen(false);
    dispatch(setSelectedBillId(null));
  };

  const handleBack = () => {
    if (viewMode !== "products") return;
    if (orderItems.length > 0) {
      setDiscardModalOpen(true);
      return;
    }
    performBack();
  };
  handleBackRef.current = handleBack;

  const handleInfo = () => {
    if (selectedItemId) {
      setModalOpen(true);
    }
  };

  const handleModify = () => {
    if (selectedItemId) {
      setModifyModalOpen(true);
    }
  };

  // Само добавките с ненулево количество (модалът може да остави празни ключове)
  const activeAddonEntries = (addons: Record<number, Record<number, number>>) =>
    Object.entries(addons)
      .map(
        ([addonId, qtyMap]) =>
          [
            addonId,
            Object.entries(qtyMap)
              .filter(([, qty]) => qty > 0)
              .sort((a, b) => Number(a[0]) - Number(b[0])),
          ] as const,
      )
      .filter(([, entries]) => entries.length > 0)
      .sort((a, b) => Number(a[0]) - Number(b[0]));

  // Добавка с брояч (тип "1", "по избор") — количеството ѝ е АБСОЛЮТНО за реда,
  // затова редове с такава добавка НИКОГА не се сливат (сливането е двусмислено).
  const hasCounterAddon = (addons: Record<number, Record<number, number>>) =>
    activeAddonEntries(addons).some(([addonId]) => {
      const addon = allAddons.find((a) => a.id === Number(addonId));
      return String(addon?.type) === "1";
    });

  const sameModifiers = (a: number[], b: number[]) =>
    a.length === b.length &&
    [...a].sort((x, y) => x - y).join(",") ===
      [...b].sort((x, y) => x - y).join(",");

  const sameAddons = (
    a: Record<number, Record<number, number>>,
    b: Record<number, Record<number, number>>,
  ) =>
    JSON.stringify(activeAddonEntries(a)) ===
    JSON.stringify(activeAddonEntries(b));

  const handleModifyAdd = (data: {
    quantity: number;
    selectedAddonItems: Record<number, Record<number, number>>;
    selectedModifiers: number[];
    comment: string;
  }) => {
    if (!selectedItemId) return;
    // Обеден продукт с дневен лимит: не повече от останалите порции
    if (lunchLimits.allowedQuantity(selectedItemId, data.quantity) < data.quantity) return;

    // Сливаме с ПОСЛЕДНИЯ ред само ако е АБСОЛЮТНО същият (продукт + модификатори
    // + добавки + коментар) — така А,А,А дава един ред ×3, но А,Б,А остава 3 реда
    // (редът на маркиране се пази). Редове с добавка с брояч (тип 1) не се сливат.
    const last = orderItems[orderItems.length - 1];
    if (
      last &&
      last.itemId === selectedItemId &&
      !hasCounterAddon(data.selectedAddonItems) &&
      !hasCounterAddon(last.addons) &&
      sameModifiers(last.modifiers, data.selectedModifiers) &&
      sameAddons(last.addons, data.selectedAddonItems) &&
      (last.comment || "") === (data.comment || "")
    ) {
      const merged = [...orderItems];
      merged[merged.length - 1] = {
        ...last,
        quantity: last.quantity + data.quantity,
      };
      setOrderItems(merged);
    } else {
      const newOrderItem: OrderItem = {
        itemId: selectedItemId,
        quantity: data.quantity,
        modifiers: data.selectedModifiers,
        addons: data.selectedAddonItems,
        comment: data.comment,
      };
      setOrderItems([...orderItems, newOrderItem]);
    }
    setModifyAddSignal((s) => s + 1);
  };

  // Двоен клик на ред от историята на масата — отваря „Промени" за същия
  // продукт като shortcut за повторна поръчка.
  const handleOrderRowReorder = async (productId: number, lunchMenuId?: number | null) => {
    // Закриването на масата още чака принтера — сметката не приема поръчки.
    if (isSelectedBillClosing) return;
    // Ред от обедно меню се поръчва пак САМО от обедното и само докато то е
    // активно — проверява се свежо (друг терминал може да го е приключил).
    // Свършило → не се допоръчва (нито на обедна, нито тихо на основна цена).
    if (lunchMenuId != null) {
      await checkLunchMenus(0);
      if (!isLunchItemOnSale(queryClient.getQueryData(["menu"]), lunchMenuId, productId)) {
        message.warning(t("lunchMenu.reorderEnded"));
        return;
      }
      if (lunchLimits.isSoldOutFresh(queryClient.getQueryData<MenuResponse>(["menu"]), lunchMenuId, productId))
        return;
      setSelectedItemId(lunchItemId(lunchMenuId, productId));
      setReorderMode(true);
      setModifyModalOpen(true);
      return;
    }
    const item = allItems.find((i) => i.id === productId);
    if (!item) {
      message.warning(t("ordering.reorderUnavailable"));
      return;
    }
    setSelectedItemId(item.id);
    setReorderMode(true);
    setModifyModalOpen(true);
  };
  orderRowReorderRef.current = handleOrderRowReorder;

  // Потвърждение от „Промени" в режим повторна поръчка — праща поръчката
  // директно към избраната сметка (без кошница и без смяна на изгледа).
  const handleReorderAdd = async (data: {
    quantity: number;
    selectedAddonItems: Record<number, Record<number, number>>;
    selectedModifiers: number[];
    comment: string;
  }) => {
    if (!selectedItemId || !selectedBillId) return;
    if (createBillMutation.isPending) return;
    // Повторната поръчка отива направо в сметката — кошницата не се брои
    if (lunchLimits.allowedQuantity(selectedItemId, data.quantity, 0) < data.quantity) return;

    try {
      // В try: продукт, изчезнал от каталога (напр. свършило обедно меню),
      // хвърля — показваме грешка вместо тихо засядане
      const elements = buildBillElements([
        {
          itemId: selectedItemId,
          quantity: data.quantity,
          modifiers: data.selectedModifiers,
          addons: data.selectedAddonItems,
          comment: data.comment,
        },
      ]);
      await createBillMutation.mutateAsync({
        billId: selectedBillId,
        elements,
        comment: "",
      });
      message.success(t("ordering.productsAddedSuccess"));
      // Обедни порции → свежият брой остават за всички продукти
      if (elements.some((element) => element.lunchMenuId != null)) void checkLunchMenus(0);
    } catch (err: unknown) {
      if (await handleLunchEnded(err)) return;
      // При мрежова грешка `detail` от BE-то няма и общото „Грешка" не казва
      // нищо. Поръчката е МУТАЦИЯ и не се повтаря сама (виж `api/client.ts`
      // §Повторение) — затова текстът праща човека да ПОГЛЕДНЕ масата,
      // вместо да натисне пак наслука.
      message.error(
        getApiErrorMessage(
          err,
          isCloudUnreachableError(err)
            ? t("ordering.orderCloudUnreachable")
            : t("common.error"),
        ),
      );
    }
  };

  // Сървърът отказа обеден продукт (409 `lunch_menu_ended`): менюто е свършило
  // или друг терминал го е приключил/изтрил. Свежо меню → групата изчезва, а
  // кошницата минава на основната цена (useEndedLunchCartItems); сервитьорът
  // вижда какво е станало и поръчва наново.
  // 409 `lunch_limit_reached`: порциите свършиха (друг сервитьор взе
  // последните) — свеж брой и какво остава; поръчката не е записана.
  const handleLunchEnded = async (err: unknown) => {
    const limitMessage = lunchLimits.limitErrorMessage(err);
    if (limitMessage != null) {
      await checkLunchMenus(0);
      message.warning(limitMessage);
      return true;
    }
    if (getEndedLunchMenuIds(err) == null) return false;
    await checkLunchMenus(0);
    message.warning(t("lunchMenu.orderEnded"));
    return true;
  };

  // Строи payload елементите за CreateBillIn от редове на кошницата —
  // ползва се и от подаването на кошницата, и от бързата повторна поръчка.
  const buildBillElements = (items: OrderItem[]) =>
    items.map((orderItem) => {
      const item = allItems.find((i) => i.id === orderItem.itemId);
      if (!item) throw new Error(`Item ${orderItem.itemId} not found`);

      // stationIds е гарантирано непразен (виж Item) — ?? 1 е типова застраховка
      const stationId = item.stationIds?.[0] ?? 1;

      return {
        // Копие от обедно меню → истинският артикул/рецепта + обедното меню
        // (цената по-долу е обедната) — виж lib/lunchCatalog.ts
        productId: sourceItemIdOf(item),
        ...(item.lunchMenuId != null ? { lunchMenuId: item.lunchMenuId } : {}),
        stationId: stationId,
        quantity: orderItem.quantity.toString(),
        price: item.price.toString(),
        lockedPrice: item.lockedPrice,
        percent: "0",
        lineTotal: (item.price * orderItem.quantity).toString(),
        status: "0",
        modifiers: [
          ...orderItem.modifiers.map((modId) => ({
            modifier_id: modId.toString(),
          })),
          ...(orderItem.comment ? [{ modifier_id: orderItem.comment }] : []),
        ],
        addons: Object.entries(orderItem.addons).flatMap(([addonId, qtyMap]) =>
          Object.entries(qtyMap).map(([addonItemId, addonQty]) => {
            const itemId = Number(addonItemId);
            const addon = allAddons.find((a) => a.id === Number(addonId));
            const addonItemData = addon?.items.find(
              (ai) => ai.itemId === itemId,
            );
            const addonPrice =
              addonItemData?.addonItemPrice ?? addonItemData?.price ?? 0;
            const addonItem = allItems.find((i) => i.id === itemId);
            const addonStationId = addonItem?.stationIds?.[0] ?? 1;
            // Изпращаме ФИНАЛНОТО количество (бекендът не умножава):
            // тип "1" (обикновена) — броячът от модала е АБСОЛЮТЕН (не зависи
            // от бройката на продукта); тип "2"/"3" — по 1 на продукт (легаси).
            const absolute = String(addon?.type) === "1";
            const effectiveQty = absolute
              ? addonQty
              : addonQty * orderItem.quantity;
            return {
              addition_item_id: itemId,
              station_id: addonStationId,
              quantity: effectiveQty.toString(),
              price: addonPrice.toString(),
              percent: "0",
              total_sum: (addonPrice * effectiveQty).toString(),
            };
          }),
        ),
      };
    });

  const handleConfirmOrder = async (comment: string) => {
    try {
      // В try: продукт, изчезнал от каталога, хвърля — виж handleReorderAdd
      const billData: CreateBillIn = {
        billId: selectedBillId || 0,
        tableName: newTableName || undefined,
        elements: buildBillElements(orderItems),
        comment: comment || "",
        // Клиент, избран преди откриването (нова маса) — BE го закача веднага
        // след създаването на сметката
        discount:
          !selectedBillId && pendingDiscount ? pendingDiscount : undefined,
        // Брой гости от модала преди „Нова маса" — само за новата сметка
        guests:
          !selectedBillId && newTableGuests != null ? newTableGuests : undefined,
      };

      // Запазваме response-а за да вземем billId на новата/обновена сметка
      const result = await createBillMutation.mutateAsync(billData);
      // Обедни порции → свежият брой остават (виж handleReorderAdd)
      if (billData.elements.some((element) => element.lunchMenuId != null))
        void checkLunchMenus(0);
      // При лична номерация „Нова маса" може да влезе в празната сметка от
      // „Настани" (резервации) — тогава сметката вече беше в списъка.
      const joinedExistingBill =
        newTableName != null && bills.some((bill) => bill.id === result.id);
      message.success(
        !newTableName
          ? t("ordering.productsAddedSuccess")
          : joinedExistingBill
            ? t("ordering.tableJoinedSuccess", { name: newTableName })
            : t("ordering.tableCreatedSuccess", { name: newTableName }),
      );
      setOrderItems([]);
      setSelectedItemId(null);
      setSelectedCategoryId(null);
      setNewTableName(null);
      setPendingDiscount(null);
      setOrderComment("");
      setShowCommentInput(false);
      setSheetOpen(false);
      // Задаваме selectedBillId на новата/обновена сметка за да остане фокусът върху нея
      dispatch(setSelectedBillId(result.id));
      setViewMode("tables");
      // Бързо закриване: веднага след поръчката отваряме модала „Закрий" за
      // същата сметка (TableFooter изчаква тя да се появи в bills списъка).
      if (quickCloseEnabled) setPendingCloseBillId(result.id);
    } catch (err: unknown) {
      if (await handleLunchEnded(err)) return;
      // Проверка дали грешката е за дублирано име на маса
      const errorMessage =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || String(err);
      const isDuplicateTableError =
        errorMessage.toLowerCase().includes("table") &&
        (errorMessage.toLowerCase().includes("exist") ||
          errorMessage.toLowerCase().includes("duplicate") ||
          errorMessage.toLowerCase().includes("already"));

      if (isDuplicateTableError && newTableName) {
        // Показваме грешка за дублирана маса и отваряме модала отново
        message.error(t("ordering.tableAlreadyExists", { name: newTableName }));
        // Изчистваме името и отваряме модала отново
        setNewTableName(null);
        setNewTableModalOpen(true);
        setViewMode("tables");
      } else {
        // Обща грешка — локализирана, не суровият detail. Мрежовата грешка си
        // има свой текст: тя не значи „отказано", а „не се знае".
        message.error(
          getApiErrorMessage(
            err,
            isCloudUnreachableError(err)
              ? t("ordering.orderCloudUnreachable")
              : t("common.error"),
          ),
        );
      }
    }
  };

  // Подава поръчката директно от панела/sheet-а (и от Enter). Guard-ове:
  // празна кошница → предупреждение; заявка в движение → игнорираме (пази от
  // двойно подаване, тъй като Enter вече стреля директно).
  const handleSubmitOrder = () => {
    if (orderItems.length === 0) {
      message.warning(t("ordering.selectProductsWarning"));
      return;
    }
    if (createBillMutation.isPending) return;
    void handleConfirmOrder(orderComment);
  };
  submitOrderRef.current = handleSubmitOrder;

  // Toggle на полето за коментар към поръчката (затварянето чисти текста —
  // същото поведение като стария summary footer).
  const handleToggleComment = () => {
    if (showCommentInput) {
      setShowCommentInput(false);
      setOrderComment("");
    } else {
      setShowCommentInput(true);
    }
  };

  const handleOrderItemQuantityChange = (index: number, delta: number) => {
    const newOrderItems = [...orderItems];
    const item = newOrderItems[index];

    let actualDelta = delta;
    // „+" на ред в кошницата: обеден продукт с дневен лимит — не повече от
    // останалите порции (както „+" в каталога)
    if (delta > 0 && lunchLimits.allowedQuantity(item.itemId, delta) < delta) return;

    // При намаляване, специална логика
    if (delta < 0) {
      if (item.quantity === 0.5) {
        // Ако е точно 0.5, махаме 0.5
        actualDelta = -0.5;
      }
      // Иначе винаги махаме 1 (дори да стане отрицателно)
    }

    const newQuantity = item.quantity + actualDelta;

    if (newQuantity <= 0) {
      // Премахваме елемента ако количеството стане 0 или по-малко
      newOrderItems.splice(index, 1);
    } else {
      // Актуализираме количеството
      item.quantity = newQuantity;
    }

    setOrderItems(newOrderItems);
  };

  const handleNewTable = async () => {
    // „Отказ" в „Брой гости" спира „Нова маса" изцяло
    if (!(await askBeforeNewTable())) return;
    if (!quickCloseEnabled) {
      setNewTableModalOpen(true);
      return;
    }
    await openNextTable();
  };

  const handleNewTableSubmit = (tableName: string) => {
    setNewTableName(tableName);
    dispatch(setSelectedBillId(null));
    // Чисти евентуална остатъчна селекция от повторна поръчка в изгледа с маси
    setSelectedItemId(null);
    setViewMode("products");
  };

  const handleAddToTable = () => {
    // Масата, чието закриване чака принтера, не приема поръчки (и F6, и бутонът
    // във футъра са заключени — това е последната преграда).
    if (selectedBillId && !isSelectedBillClosing) {
      setSelectedItemId(null);
      setViewMode("products");
    }
  };

  const handleTableSelect = (billId: number) => {
    queryClient.invalidateQueries({ queryKey: ["bills"] });
    dispatch(setSelectedBillId(billId));
  };

  // Селекция (вкл. keyboard фокус-sync на десктоп) — НЕ отваря модал.
  const handleItemSelect = (itemId: number | null) => {
    setSelectedItemId(itemId);
  };

  const handleQuantityChange = (itemId: number, newTotalQuantity: number) => {
    // Намираме всички записи за този продукт
    const allItemRecords = orderItems.filter((item) => item.itemId === itemId);

    // Изчисляваме текущото общо количество
    const currentTotal = allItemRecords.reduce(
      (sum, item) => sum + item.quantity,
      0,
    );

    // Обеден продукт с дневен лимит: не повече от останалите порции
    let targetTotal = newTotalQuantity;
    if (targetTotal > currentTotal) {
      const allowed = lunchLimits.allowedQuantity(itemId, targetTotal - currentTotal, currentTotal);
      if (allowed <= 0) return;
      targetTotal = currentTotal + allowed;
    }

    // При инкремент проверяваме за задължителни (type === "2") / препоръчителни
    // (type === "3") добавки или задължителен модификатор (isRequired)
    if (targetTotal > currentTotal && itemsRequiringAddons.has(itemId)) {
      // Селектираме продукта и отваряме ModifyItemModal
      setSelectedItemId(itemId);
      setModifyModalOpen(true);
      return;
    }
    // Изчисляваме разликата
    const diff = targetTotal - currentTotal;

    // "Прост" запис = без модификатори, без добавки, без коментар (създаден от "+")
    const isSimpleRecord = (item: OrderItem) =>
      item.modifiers.length === 0 &&
      Object.keys(item.addons).length === 0 &&
      !item.comment;

    if (diff > 0) {
      // Инкремент — сливаме с ПОСЛЕДНИЯ ред, ако той е същият продукт без
      // добавки/модификатори/коментар (какъвто създава "+"): 3ד+" = един ред ×3.
      // Ако горният ред е друг продукт (или има добавки/модификатори) — нов ред,
      // за да се пази редът на маркиране (А,Б,А = 3 реда).
      const last = orderItems[orderItems.length - 1];
      if (last && last.itemId === itemId && isSimpleRecord(last)) {
        const merged = [...orderItems];
        merged[merged.length - 1] = {
          ...last,
          quantity: last.quantity + diff,
        };
        setOrderItems(merged);
      } else {
        const newOrderItem: OrderItem = {
          itemId,
          quantity: diff,
          modifiers: [],
          addons: {},
          comment: "",
        };
        setOrderItems([...orderItems, newOrderItem]);
      }
    } else if (diff < 0) {
      // Декремент — махаме от последно добавените записи назад:
      // първо от "простите" (създадени от "+"), после от тези с добавки/модификатори.
      const newOrderItems = [...orderItems];
      let leftToDecrement = Math.abs(diff);

      const drainFromEnd = (onlySimple: boolean) => {
        for (
          let i = newOrderItems.length - 1;
          i >= 0 && leftToDecrement > 0;
          i--
        ) {
          const record = newOrderItems[i];
          if (record.itemId !== itemId) continue;
          if (onlySimple && !isSimpleRecord(record)) continue;

          if (record.quantity > leftToDecrement) {
            record.quantity -= leftToDecrement;
            leftToDecrement = 0;
          } else {
            leftToDecrement -= record.quantity;
            newOrderItems.splice(i, 1);
          }
        }
      };

      drainFromEnd(true);
      drainFromEnd(false);

      setOrderItems(newOrderItems);
    }
  };

  return (
    <>
      <OrderingShell>
        {/* Горната лента е при масите и (под десктоп прага) при продуктите —
            там стрелката ѝ е единственият „назад". На десктоп продуктовият
            изглед е без лента: Назад/профил/Backoffice са във футъра. */}
        {(viewMode === "tables" || !isDesktop) && (
          <OrderingTopBar
            onBack={viewMode === "products" ? handleBack : undefined}
          />
        )}

        {viewMode === "tables" ? (
          <>
            {/* Резервираното място за фиксирания футър е в цвета на панела
                (иначе прозира ивица pos-canvas) и съответства на реалната му
                височина: тесен екран 63px (pt-2 + 46px бутони + pb-8 + border),
                от sm нагоре 77px (py-3 + 52px бутони + border) */}
            <div className="flex flex-1 overflow-hidden pb-[calc(63px+env(safe-area-inset-bottom,0px)*0.5)] sm:pb-[calc(77px+env(safe-area-inset-bottom,0px)*0.5)] min-h-0 bg-pos-panel">
              {/* Sidebar с маси - вляво (на тесен екран може да е скрит с двоен
                  клик); под списъка на десктоп е часовникът */}
              <TableRail hidden={railHidden && isNarrow}>
                {billsLoading && tableBills.length === 0 ? (
                  <TableRailSkeleton />
                ) : (
                  <TableList
                    bills={tableBills}
                    closingBillIds={closingBillIds}
                    selectedBillId={selectedBillId}
                    onTableSelect={handleTableSelect}
                    onTableKeyboardSelect={(billId) =>
                      dispatch(setSelectedBillId(billId))
                    }
                    onVoiceOrder={
                      canVoiceOrder ? () => setVoiceModalOpen(true) : undefined
                    }
                    voiceLocked={edgeLocal}
                    onTableDoubleClick={
                      isNarrow
                        ? (billId) => {
                            handleTableSelect(billId);
                            setRailHidden(true);
                          }
                        : undefined
                    }
                    showClosedTables={showClosedTables}
                    isModalOpen={tablesModalOpen}
                    itemsKeyboardActive={orderItemsKeyboardActive}
                    onArrowRightEdge={handleArrowRightEdge}
                  />
                )}
              </TableRail>

              {/* Main content - детайли на поръчките - вдясно */}
              <main className="flex-1 overflow-hidden bg-pos-canvas">
                <OrderDetails
                  bill={selectedBill}
                  selectedItemId={selectedOrderItemId}
                  onItemSelect={setSelectedOrderItemId}
                  onItemReorder={handleOrderRowReorder}
                  onShowTables={
                    railHidden && isNarrow
                      ? () => setRailHidden(false)
                      : undefined
                  }
                  summarized={billSummarized}
                  onToggleSummarized={toggleBillSummarized}
                />
              </main>
            </div>

            {/* Footer за маси */}
            <div className="fixed bottom-0 left-0 right-0 z-50">
              <TableFooter
                onNewTable={handleNewTable}
                onAddToTable={handleAddToTable}
                selectedTableId={selectedBillId}
                selectedTableName={selectedBill?.name}
                onClearSelection={() => setSelectedOrderItemId(null)}
                onRenameModalOpenChange={setRenameTableModalOpen}
                onAnyModalOpenChange={setFooterModalOpen}
                autoCloseBillId={pendingCloseBillId}
                onAutoCloseHandled={() => setPendingCloseBillId(null)}
                onReservationSeated={handleReservationSeated}
              />
            </div>
          </>
        ) : (
          <>
            <div
              className={`flex flex-1 overflow-hidden min-h-0 ${
                isDesktop
                  ? "pb-[66px]"
                  : "pb-[calc(52px+env(safe-area-inset-bottom,0px)*0.5)]"
              }`}
            >
              {/* Основна колона: категорийни chips + търсачка + продуктов списък */}
              <main className="flex-1 flex flex-col overflow-hidden bg-pos-canvas min-w-0">
                {/* На телефон отделният хедър (Назад/„Добави"/маса) е премахнат
                    (0.0.117) — назад е стрелката в горната лента, а името на
                    масата стои до категорийния селектор (66/33) */}
                {/* Над каталога: групите (дърво) или станциите (плосък ред)
                    според настройката на ресторанта catalogFilter — на десктоп;
                    под прага винаги свитият селектор на групите */}
                {isDesktop && catalogFilter === "stations" ? (
                  <StationChips
                    selectedStationId={selectedStationId}
                    onStationSelect={setSelectedStationId}
                  />
                ) : (
                  <CategoryChips
                    selectedCategoryId={selectedCategoryId}
                    onCategorySelect={setSelectedCategoryId}
                    collapsed={!isDesktop}
                    trailing={
                      !isDesktop && tableLabel ? (
                        // Името на масата — статична карта като селектора:
                        // акцентна лента вляво, kicker „МАСА" + името отдолу
                        <div className="h-10 min-w-0 flex items-center gap-2.5 rounded-xl bg-pos-card overflow-hidden">
                          <span
                            aria-hidden
                            className="w-1.5 self-stretch bg-pos-accent shrink-0"
                          />
                          <div className="min-w-0 flex flex-col justify-center gap-0.5 pr-3 leading-none">
                            <span className="font-pos-mono text-[9px] font-semibold tracking-[.14em] uppercase text-pos-sec-head">
                              {t("ordering.tableKicker")}
                            </span>
                            <span className="text-[15px] font-extrabold text-pos-ink truncate">
                              {tableLabel}
                            </span>
                          </div>
                        </div>
                      ) : undefined
                    }
                  />
                )}

                <div className="flex-1 min-h-0 overflow-hidden">
                  <ItemCatalog
                    categoryId={selectedCategoryId}
                    selectedItemId={selectedItemId}
                    onItemSelect={handleItemSelect}
                    isModalOpen={
                      modalOpen ||
                      modifyModalOpen ||
                      discardModalOpen ||
                      sheetOpen ||
                      clientsModalOpen ||
                      editModifiersOpen
                    }
                    orderItems={orderItems}
                    onQuantityChange={handleQuantityChange}
                    onItemEdit={orderLineEdit.openFromCatalog}
                    onBack={handleBack}
                    itemsRequiringAddons={itemsRequiringAddons}
                    addonModalAddSignal={modifyAddSignal}
                    view={catalogView}
                    sort={configuration.catalogSort}
                    billDiscountPercent={billDiscount?.percent ?? 0}
                    stationId={isDesktop ? selectedStationId : null}
                    colorBy={configuration.colorBy}
                    searchFieldVisible={configuration.orderingSearchField}
                    searchScope={configuration.orderingSearchScope}
                    onExitCategory={() => setSelectedCategoryId(null)}
                    keyboardOpen={keyboardOpen}
                    keyboardLayout={configuration.keyboardLayout}
                    onKeyboardClose={() => setKeyboardOpen(false)}
                  />
                </div>
              </main>

              {/* Постоянен панел „Преглед на поръчката" — само на десктоп */}
              {isDesktop && (
                <OrderPanel
                  orderItems={orderItems}
                  comment={orderComment}
                  showCommentInput={showCommentInput}
                  onCommentChange={setOrderComment}
                  onToggleComment={handleToggleComment}
                  onQuantityChange={handleOrderItemQuantityChange}
                  onItemEdit={orderLineEdit.openFromPanel}
                  onSubmit={handleSubmitOrder}
                  submitting={createBillMutation.isPending}
                  billDiscount={billDiscount}
                  previewFilterKind={previewFilterKind}
                  previewFilter={previewFilter}
                  onPreviewFilterChange={handlePreviewFilterChange}
                  colorBy={configuration.colorBy}
                />
              )}
            </div>

            {/* Долен стек: футърът с бутони е САМО на десктоп (на телефон
                Назад = стрелката в хедъра, Инфо е в „Промени", а „Промени"
                се отваря с клик на продукт) + мобилната лента на sheet-а */}
            <div className="fixed bottom-0 left-0 right-0 z-50 flex flex-col">
              {isDesktop && (
                <OrderFooter
                  onBack={handleBack}
                  onInfo={handleInfo}
                  onModify={handleModify}
                  hasSelectedItem={!!selectedItemId}
                  onClients={
                    canSelectCustomer
                      ? () => setClientsModalOpen(true)
                      : undefined
                  }
                  clientsDisabled={!selectedBillId && !newTableName}
                  catalogView={catalogView}
                  onCatalogViewChange={handleCatalogViewChange}
                  keyboardOpen={keyboardOpen}
                  onToggleKeyboard={() => setKeyboardOpen((open) => !open)}
                  tableName={tableLabel}
                />
              )}
              {!isDesktop && (
                <MobileOrderBar
                  orderItems={orderItems}
                  onClick={() => setSheetOpen(true)}
                  billDiscountPercent={billDiscount?.percent ?? 0}
                />
              )}
            </div>

            {/* Мобилен bottom sheet „Преглед на поръчката" */}
            <OrderSheet
              open={sheetOpen && !isDesktop}
              onClose={() => setSheetOpen(false)}
              orderItems={orderItems}
              comment={orderComment}
              showCommentInput={showCommentInput}
              onCommentChange={setOrderComment}
              onToggleComment={handleToggleComment}
              onQuantityChange={handleOrderItemQuantityChange}
              onItemEdit={orderLineEdit.openFromPanel}
              onSubmit={handleSubmitOrder}
              submitting={createBillMutation.isPending}
              billDiscount={billDiscount}
            />

            {/* „Клиенти" от футъра на продуктовия изглед — преизползва модала
                на екрана с масите; отстъпката се прилага веднага по сметката,
                а каталогът/панелът показват коригираните цени */}
            <ClientsModal
              open={clientsModalOpen}
              onClose={() => setClientsModalOpen(false)}
              billId={selectedBillId}
              pendingDiscount={pendingDiscount}
              onPendingSelect={setPendingDiscount}
            />
          </>
        )}

        {/* Modal за детайли на избран артикул */}
        {selectedItem && (
          <ItemDetailModal
            item={selectedItem}
            open={modalOpen}
            onClose={() => setModalOpen(false)}
          />
        )}

        {/* Modal за редакция на количество/модификатори/коментар на ред —
            моливът в „Преглед на поръчката" и в степера на каталога */}
        <EditModifiersModal
          item={
            editModifiersTarget
              ? (allItems.find(
                  (item) => item.id === editModifiersTarget.itemId,
                ) ?? null)
              : null
          }
          open={editModifiersOpen}
          initial={editModifiersTarget}
          focusField={editModifiersTarget?.focusField}
          onClose={orderLineEdit.close}
          onSave={orderLineEdit.save}
        />

        {/* Modal за промяна на артикул */}
        {selectedItem && (
          <ModifyItemModal
            item={selectedItem}
            open={modifyModalOpen}
            onClose={() => {
              setModifyModalOpen(false);
              setReorderMode(false);
            }}
            onAdd={reorderMode ? handleReorderAdd : handleModifyAdd}
            confirmLabelKey={reorderMode ? "ordering.modifyOrder" : undefined}
            onInfo={!isDesktop ? handleInfo : undefined}
          />
        )}

        {/* Modal за потвърждаване на излизане при маркирани продукти */}
        <DiscardOrderModal
          open={discardModalOpen}
          onCancel={() => setDiscardModalOpen(false)}
          onConfirm={() => {
            setDiscardModalOpen(false);
            performBack();
          }}
        />

        {/* Modal за гласова поръчка (говориш → системата маркира) */}
        <VoiceOrderModal
          open={voiceModalOpen}
          onClose={() => setVoiceModalOpen(false)}
          personalNumberingEnabled={personalNumberingEnabled}
          currentUserId={currentUserId}
          onSeatReservation={voiceSeat.onSeatReservation}
        />
        {voiceSeat.seatModal}

        {/* Modal за нова маса */}
        <NewTableModal
          open={newTableModalOpen}
          onClose={() => setNewTableModalOpen(false)}
          onSubmit={handleNewTableSubmit}
        />
        {guestCountModal}

        {reservationConfirmModal}
      </OrderingShell>
    </>
  );
};

export default Ordering;
